import React, { useEffect, useMemo, useState } from 'react';
import Icon from '@/components/AppIcon';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Select from '@/components/ui/Select';
import {
  calculateVacationDaysByYear,
  sumVacationDays,
  normalizeRemainingVacationDays,
  formatVacationDays,
  formatVacationBreakdown,
} from '../utils/vacationDays';
import {
  clampDateToRange,
  getAbsenceDateLimits,
  getLowerBoundDate,
  getUpperBoundDate,
  formatIsoDate,
  parseDateValue,
} from '../utils/dateLimits';

const DEFAULT_FORM = {
  type: 'vacation',
  startDate: '',
  endDate: '',
  dayPart: 'full-day',
  reason: '',
};

const TYPE_OPTIONS = [
  { label: 'Vacation', value: 'vacation' },
  { label: 'Sick', value: 'sick' },
  { label: 'Emergency', value: 'emergency' },
];

const DAY_PART_OPTIONS = [
  { label: 'Full Day', value: 'full-day' },
  { label: 'Forenoon (AM)', value: 'forenoon' },
  { label: 'Afternoon (PM)', value: 'afternoon' },
];

const MIN_REASON_LENGTH = 5;

const CreateRequestModal = ({
  isOpen,
  onClose,
  onSubmit,
  employee,
  isSubmitting = false,
  canSubmit = true,
}) => {
  const [formValues, setFormValues] = useState(DEFAULT_FORM);
  const [error, setError] = useState('');
  const { minDate, maxDate, minDateString, maxDateString } = useMemo(getAbsenceDateLimits, []);

  useEffect(() => {
    if (!isOpen) {
      setFormValues(DEFAULT_FORM);
      setError('');
    }
  }, [isOpen]);

  const calendarDuration = useMemo(() => {
    if (!formValues.startDate || !formValues.endDate) {
      return 0;
    }
    const start = new Date(formValues.startDate);
    const end = new Date(formValues.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) {
      return 0;
    }
    const isSameDay = start.toDateString() === end.toDateString();
    if (isSameDay && formValues.dayPart !== 'full-day') {
      return 0.5;
    }
    const diffMs = end.getTime() - start.getTime();
    return Math.max(1, Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1);
  }, [formValues.dayPart, formValues.endDate, formValues.startDate]);

  const normalizedRemainingVacation = useMemo(() => {
    const primary = normalizeRemainingVacationDays(
      employee?.remainingVacationDaysPerYear,
    );

    const rawFallback = normalizeRemainingVacationDays(
      employee?.raw?.remainingVacationDaysPerYear,
    );

    if (Object.keys(rawFallback).length === 0) {
      return primary;
    }

    return { ...rawFallback, ...primary };
  }, [employee?.raw?.remainingVacationDaysPerYear, employee?.remainingVacationDaysPerYear]);

  const hireDate = useMemo(() => {
    const candidates = [
      employee?.raw?.hireDate,
      employee?.raw?.startDate,
      employee?.hireDate,
    ];
    for (const candidate of candidates) {
      if (!candidate || candidate === '—') {
        continue;
      }
      const parsed = new Date(candidate);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed;
      }
    }
    return null;
  }, [employee]);

  const fallbackVacationAllocation = useMemo(() => {
    const candidate = employee?.vacationDaysPerYear;
    const numericCandidate =
      typeof candidate === 'number'
        ? candidate
        : typeof candidate === 'string'
          ? Number(candidate)
          : null;

    if (typeof numericCandidate === 'number' && Number.isFinite(numericCandidate) && numericCandidate >= 0) {
      return Math.round(numericCandidate * 100) / 100;
    }
    return null;
  }, [employee?.vacationDaysPerYear]);

  const vacationBreakdown = useMemo(
    () =>
      formValues.type === 'vacation'
        ? calculateVacationDaysByYear(
            formValues.startDate,
            formValues.endDate,
            formValues.dayPart ?? 'full-day',
          )
        : {},
    [formValues.dayPart, formValues.endDate, formValues.startDate, formValues.type],
  );

  const resolvedRemainingVacation = useMemo(() => {
    if (typeof fallbackVacationAllocation !== 'number' || fallbackVacationAllocation < 0) {
      return normalizedRemainingVacation;
    }

    const requestedYears = Object.keys(vacationBreakdown);
    if (requestedYears.length === 0) {
      return normalizedRemainingVacation;
    }

    const MS_PER_DAY = 24 * 60 * 60 * 1000;
    const hireYear = hireDate?.getFullYear() ?? null;

    const getDaysInYear = (year) => {
      const start = Date.UTC(year, 0, 1);
      const end = Date.UTC(year + 1, 0, 1);
      return Math.max(1, Math.round((end - start) / MS_PER_DAY));
    };

    const calculateDefaultBalance = (year) => {
      if (!Number.isFinite(year)) {
        return null;
      }
      if (!hireDate || hireYear !== year) {
        return fallbackVacationAllocation;
      }

      const hireUtc = Date.UTC(year, hireDate.getMonth(), hireDate.getDate());
      const endOfYearUtc = Date.UTC(year, 11, 31);
      if (hireUtc > endOfYearUtc) {
        return 0;
      }

      const remainingDays = Math.max(0, Math.floor((endOfYearUtc - hireUtc) / MS_PER_DAY) + 1);
      const totalDays = getDaysInYear(year);
      if (remainingDays <= 0 || totalDays <= 0) {
        return 0;
      }

      const prorated = Math.ceil((remainingDays / totalDays) * fallbackVacationAllocation);
      return Math.min(fallbackVacationAllocation, Math.max(0, prorated));
    };

    let mutated = false;
    const merged = { ...normalizedRemainingVacation };

    requestedYears.forEach((yearKey) => {
      if (merged[yearKey] !== undefined) {
        return;
      }
      const yearNumber = Number.parseInt(yearKey, 10);
      const defaultValue = calculateDefaultBalance(yearNumber);
      if (typeof defaultValue === 'number' && defaultValue >= 0) {
        merged[yearKey] = Math.round(defaultValue * 100) / 100;
        mutated = true;
      }
    });

    return mutated ? merged : normalizedRemainingVacation;
  }, [fallbackVacationAllocation, hireDate, normalizedRemainingVacation, vacationBreakdown]);

  const vacationDuration = useMemo(
    () => (formValues.type === 'vacation' ? sumVacationDays(vacationBreakdown) : 0),
    [formValues.type, vacationBreakdown],
  );

  const vacationValidation = useMemo(() => {
    if (formValues.type !== 'vacation') {
      return { isValid: true, breakdown: {}, totalDays: 0 };
    }

    const totalDays = vacationDuration;

    if (!Number.isFinite(totalDays) || totalDays <= 0) {
      return {
        isValid: false,
        reason: 'no-weekdays',
        breakdown: vacationBreakdown,
        totalDays: 0,
      };
    }

    for (const [year, value] of Object.entries(vacationBreakdown)) {
      const requested = Number(value);
      if (!(requested > 0)) {
        continue;
      }

      const availableRaw =
        resolvedRemainingVacation[year] ??
        (typeof fallbackVacationAllocation === 'number'
          ? fallbackVacationAllocation
          : undefined);

      const normalizedAvailable =
        typeof availableRaw === 'number' && availableRaw >= 0
          ? availableRaw
          : typeof fallbackVacationAllocation === 'number' && fallbackVacationAllocation >= 0
            ? fallbackVacationAllocation
            : 0;

      if (requested - normalizedAvailable > 0.0001) {
        return {
          isValid: false,
          reason: 'insufficient',
          breakdown: vacationBreakdown,
          totalDays,
          year,
          available: normalizedAvailable,
          requested,
        };
      }
    }

    return { isValid: true, breakdown: vacationBreakdown, totalDays };
  }, [
    fallbackVacationAllocation,
    formValues.type,
    resolvedRemainingVacation,
    vacationBreakdown,
    vacationDuration,
  ]);

  const requestedDuration =
    formValues.type === 'vacation'
      ? vacationValidation.totalDays ?? 0
      : calendarDuration;

  const vacationBreakdownLabel = useMemo(() => {
    if (formValues.type !== 'vacation') {
      return '';
    }
    return formatVacationBreakdown(vacationBreakdown);
  }, [formValues.type, vacationBreakdown]);

  const vacationRestrictionMessage = useMemo(() => {
    if (formValues.type !== 'vacation') {
      return '';
    }
    if (vacationValidation.isValid) {
      return '';
    }

    switch (vacationValidation.reason) {
      case 'no-weekdays':
        return 'Vacation requests must include at least one weekday.';
      case 'insufficient':
        return `Only ${formatVacationDays(vacationValidation.available ?? 0)} remain for ${vacationValidation.year}.`;
      default:
        return 'This vacation request exceeds the remaining balance.';
    }
  }, [formValues.type, vacationValidation]);

  const vacationBalanceDisplay = useMemo(() => {
    const entries = Object.entries(resolvedRemainingVacation).sort(
      ([firstYear], [secondYear]) => Number.parseInt(firstYear, 10) - Number.parseInt(secondYear, 10),
    );

    if (entries.length > 0) {
      return entries
        .map(([year, value]) => `${year}: ${formatVacationDays(value)}`)
        .join(' • ');
    }

    if (typeof fallbackVacationAllocation === 'number') {
      return `${formatVacationDays(fallbackVacationAllocation)} per year`;
    }

    return null;
  }, [resolvedRemainingVacation, fallbackVacationAllocation]);

  const displayDurationLabel =
    requestedDuration > 0 ? formatVacationDays(requestedDuration) : 'Duration pending';

  const trimmedReason = (formValues.reason ?? '').trim();
  const reasonIsRequired = formValues.type === 'emergency' || formValues.type === 'personal';
  const isReasonOptional = !reasonIsRequired;

  const isSingleDaySelection =
    Boolean(
      formValues.startDate &&
        formValues.endDate &&
        formValues.startDate === formValues.endDate,
    );

  const startMaxDateString = useMemo(
    () => formatIsoDate(getUpperBoundDate(formValues.endDate, maxDate)),
    [formValues.endDate, maxDate],
  );

  const endMinDateString = useMemo(
    () => formatIsoDate(getLowerBoundDate(formValues.startDate, minDate)),
    [formValues.startDate, minDate],
  );

  const dayPartOptions = useMemo(
    () =>
      DAY_PART_OPTIONS.map((option) =>
        option.value === 'full-day'
          ? option
          : {
              ...option,
              disabled: !isSingleDaySelection,
            },
      ),
    [isSingleDaySelection],
  );

  const disableSubmit =
    isSubmitting ||
    !canSubmit ||
    !formValues.type ||
    !formValues.startDate ||
    !formValues.endDate ||
    requestedDuration <= 0 ||
    (formValues.type === 'vacation' && !vacationValidation.isValid) ||
    (reasonIsRequired && trimmedReason.length < MIN_REASON_LENGTH);

  if (!isOpen) {
    return null;
  }

  const handleStartDateChange = (value) => {
    setFormValues((prev) => {
      const boundedStart = clampDateToRange(value, minDate, maxDate);
      const next = { ...prev, startDate: boundedStart };

      const currentEnd = clampDateToRange(prev.endDate, minDate, maxDate);
      const startDateValue = parseDateValue(boundedStart);
      const endDateValue = parseDateValue(currentEnd);

      if (!currentEnd) {
        next.endDate = boundedStart;
      } else if (startDateValue && endDateValue && endDateValue < startDateValue) {
        next.endDate = boundedStart;
      } else {
        next.endDate = currentEnd;
      }
      if (
        next.dayPart !== 'full-day' &&
        next.startDate &&
        next.endDate &&
        next.startDate !== next.endDate
      ) {
        next.dayPart = 'full-day';
      }
      return next;
    });
  };

  const handleEndDateChange = (value) => {
    setFormValues((prev) => {
      const next = { ...prev };
      const boundedEnd = clampDateToRange(value, minDate, maxDate);
      const boundedStart = clampDateToRange(prev.startDate, minDate, maxDate);
      const startDateValue = parseDateValue(boundedStart);
      const endDateValue = parseDateValue(boundedEnd);

      next.startDate = boundedStart;

      if (!boundedEnd) {
        next.endDate = '';
      } else if (startDateValue && endDateValue && endDateValue < startDateValue) {
        next.endDate = boundedStart;
      } else {
        next.endDate = boundedEnd;
      }

      if (
        next.dayPart !== 'full-day' &&
        next.startDate &&
        next.endDate &&
        next.startDate !== next.endDate
      ) {
        next.dayPart = 'full-day';
      }

      return next;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) {
      return;
    }
    setError('');

    if (!canSubmit) {
      setError('We were unable to locate your employee profile for this request.');
      return;
    }

    if (!formValues.startDate || !formValues.endDate) {
      setError('Please choose a start and end date.');
      return;
    }

    if (new Date(formValues.endDate) < new Date(formValues.startDate)) {
      setError('End date cannot be earlier than the start date.');
      return;
    }

    if (!isSingleDaySelection && formValues.dayPart !== 'full-day') {
      setError('Half-day absences must begin and end on the same day.');
      return;
    }

    if (reasonIsRequired && trimmedReason.length < MIN_REASON_LENGTH) {
      setError('Let your manager know why you need this time away (at least 5 characters).');
      return;
    }

    if (formValues.type === 'vacation' && !vacationValidation.isValid) {
      setError(
        vacationRestrictionMessage
          || 'This vacation request exceeds your available balance.',
      );
      return;
    }

    if (requestedDuration <= 0) {
      setError('Please choose valid dates for your request.');
      return;
    }

    try {
      const submittedReason =
        trimmedReason.length > 0
          ? trimmedReason
          : formValues.type === 'emergency'
            ? 'Emergency absence request'
            : '';

      const dayPartToSubmit = isSingleDaySelection ? formValues.dayPart : 'full-day';

      await onSubmit({
        ...formValues,
        reason: submittedReason,
        dayPart: dayPartToSubmit,
        duration: requestedDuration,
      });
      setFormValues(DEFAULT_FORM);
      onClose?.();
    } catch (submissionError) {
      const message =
        submissionError?.response?.data?.message
        ?? submissionError?.message
        ?? 'Failed to submit your request. Please try again.';
      setError(message);
    }
  };

  const safeEmployeeName =
    employee?.name
    ?? employee?.fullName
    ?? 'You';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 p-4 sm:p-6">
      <div className="flex w-full max-w-2xl flex-col overflow-hidden rounded-lg border border-border bg-card shadow-elevation-4 max-h-[calc(100vh-2rem)] sm:max-h-[calc(100vh-4rem)]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-border px-6 py-4">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Request Absence</h2>
            <p className="text-sm text-muted-foreground">
              Submit a new absence request for manager review.
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={18} />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-1 flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto">
            <div className="grid gap-6 px-6 py-6 md:grid-cols-2">
              <div className="space-y-4">
                <div className="rounded-lg border border-border bg-muted/30 p-4">
                  <p className="text-sm font-semibold text-foreground">{safeEmployeeName}</p>
                  {employee?.position && (
                    <p className="text-xs text-muted-foreground">{employee.position}</p>
                  )}
                  <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-muted-foreground">
                    <div>
                      <p className="uppercase tracking-wide text-[11px] text-muted-foreground/80">
                        Primary location
                      </p>
                      <p className="font-medium text-foreground">
                        {employee?.location || '—'}
                      </p>
                    </div>
                    <div>
                      <p className="uppercase tracking-wide text-[11px] text-muted-foreground/80">
                        Vacation balance
                      </p>
                      <p className="font-medium text-foreground">
                        {vacationBalanceDisplay ?? 'Check with your manager'}
                      </p>
                    </div>
                  </div>
                </div>

                <Select
                  label="Request Type"
                  value={formValues.type}
                  onChange={(value) => setFormValues((prev) => ({ ...prev, type: value }))}
                  options={TYPE_OPTIONS}
                  required
                />

                <DatePicker
                  label="Start Date"
                  value={formValues.startDate}
                  onChange={(event) => handleStartDateChange(event?.target?.value ?? '')}
                  min={minDateString}
                  max={startMaxDateString}
                  required
                />

                <DatePicker
                  label="End Date"
                  value={formValues.endDate}
                  onChange={(event) => handleEndDateChange(event?.target?.value ?? '')}
                  min={endMinDateString}
                  max={maxDateString}
                  required
                />

                <Select
                  label="Day Portion"
                  value={formValues.dayPart}
                  onChange={(value) =>
                    setFormValues((prev) => ({
                      ...prev,
                      dayPart: value || 'full-day',
                    }))
                  }
                  options={dayPartOptions}
                  description="Half-day options are available when the start and end date are the same."
                />

                <div className="rounded-lg border border-dashed border-border px-3 py-2 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2 text-foreground">
                    <Icon name="Clock" size={16} />
                    <span>{displayDurationLabel}</span>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {formValues.type === 'vacation'
                      ? 'Weekends are excluded from the requested vacation days.'
                      : 'Duration is calculated automatically based on your selected dates.'}
                  </p>
                  {formValues.type === 'vacation' && vacationBreakdownLabel && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {`Breakdown: ${vacationBreakdownLabel}`}
                    </p>
                  )}
                  {formValues.type === 'vacation' && vacationRestrictionMessage && (
                    <p className="mt-2 text-xs text-destructive">{vacationRestrictionMessage}</p>
                  )}
                </div>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-1">
                    <span>Reason</span>
                    {isReasonOptional ? (
                      <span className="ml-1 text-xs text-muted-foreground">(optional for vacation or sick)</span>
                    ) : (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </label>
                  <textarea
                    className="min-h-[140px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    value={formValues.reason}
                    onChange={(event) =>
                      setFormValues((prev) => ({ ...prev, reason: event?.target?.value ?? '' }))
                    }
                    placeholder={
                      isReasonOptional
                        ? 'Optional notes for your manager…'
                        : 'Share the details your manager should know…'
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {isReasonOptional
                      ? 'Adding context can speed up responses, but it is not required for vacation or sick requests.'
                      : 'Provide enough context so your manager can quickly review urgent or personal requests.'}
                  </p>
                </div>

                {error && (
                  <div className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-shrink-0 items-center justify-end space-x-3 border-t border-border px-6 py-4">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Send"
              loading={isSubmitting}
              disabled={disableSubmit}
            >
              Submit Request
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateRequestModal;
